import React, { useState } from 'react';
import { 
  CreditCard,
  DollarSign, 
  XCircle,
  ExternalLink,
  Loader2,
  RefreshCw,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useUsageData } from '../contexts/UsageDataContext';
import { useBilling } from '../contexts/BillingContext';
import { formatCurrency } from '../api/usage';
import { 
  getUserProfile,
  createStripeCustomer,
  createCustomerPortalSession,
  triggerBillingJob,
  getSubscriptionInfo,
  hasActiveSubscription,
  createSubscriptionCheckout,
  syncSubscriptionStatus,
  resumeSubscription
} from '../api/billing';
import { FREE_REQUESTS_PER_MONTH, API_PRICING } from '../config/constants';
import PageHeader from './PageHeader';
import './BillingPage.css';

const BillingPage: React.FC = () => {
  const { user } = useAuth();
  const { currentMonthUsage, refreshCurrentMonthData } = useUsageData();
  const { 
    billingSettings, 
    billingInvoices,
    settingsLoading, 
    invoicesLoading,
    updateBillingSettingsData,
    loadBillingData
  } = useBilling();
  
  const [isManagingBilling, setIsManagingBilling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isTestingBilling, setIsTestingBilling] = useState(false);
  const [billingTestResult, setBillingTestResult] = useState<any>(null);
  const [historyItemsToShow, setHistoryItemsToShow] = useState(5);
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  const handleRefresh = async () => {
    if (!user) return;
    
    setIsRefreshing(true);
    console.log('[BillingPage] Refresh button clicked');
    
    try {
      // Sync subscription status from Stripe first to ensure we have latest data
      try {
        console.log('[BillingPage] Starting subscription sync...');
        await syncSubscriptionStatus(user.id);
        console.log('[BillingPage] ✅ Subscription sync completed');
      } catch (syncError) {
        console.error('[BillingPage] ❌ Failed to sync subscription status:', syncError);
        // Show user-friendly error message
        if (syncError instanceof Error) {
          alert(`Failed to sync subscription status: ${syncError.message}`);
        }
        // Continue with refresh even if sync fails
      }
      
      // Refresh both usage data and billing data
      console.log('[BillingPage] Refreshing usage and billing data...');
      await Promise.all([
        refreshCurrentMonthData(),
        loadBillingData(),
        loadSubscriptionInfo()
      ]);
      console.log('[BillingPage] ✅ All data refreshed');
    } catch (error) {
      console.error('[BillingPage] ❌ Failed to refresh billing data:', error);
      if (error instanceof Error) {
        alert(`Failed to refresh billing data: ${error.message}`);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadSubscriptionInfo = async () => {
    if (!user) return;
    
    try {
      const info = await getSubscriptionInfo(user.id);
      setSubscriptionInfo(info);
    } catch (error) {
      console.error('Failed to load subscription info:', error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Load subscription info on mount and check for success redirect
  React.useEffect(() => {
    if (user) {
      // Always sync subscription status from Stripe on page load to ensure we have latest status
      const syncAndLoad = async () => {
        setSubscriptionLoading(true);
        try {
          await syncSubscriptionStatus(user.id);
        } catch (syncError) {
          console.warn('Failed to sync subscription status, using cached value:', syncError);
          // Continue with cached value if sync fails
        }
        
        // Check if we're returning from successful subscription
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('subscription') === 'success') {
          // Remove query param from URL
          window.history.replaceState({}, '', window.location.pathname);
        }
        
        loadSubscriptionInfo();
      };
      
      syncAndLoad();
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubscribe = async () => {
    if (!user) return;
    
    setIsSubscribing(true);
    try {
      const checkoutUrl = await createSubscriptionCheckout(user.id);
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Failed to create subscription checkout:', error);
      alert(error instanceof Error ? error.message : 'Failed to start subscription. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };





  const handleManageBilling = async () => {
    if (!user) return;
    
    setIsManagingBilling(true);
    
    try {
      // After returning from Stripe portal, sync subscription status
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('returned') === 'true') {
        // Sync subscription status when returning from Stripe
        await syncSubscriptionStatus(user.id);
        await loadSubscriptionInfo();
        // Remove query param
        window.history.replaceState({}, '', window.location.pathname);
      }
      
      const userEmail = user.email;
      
      if (!userEmail) {
        throw new Error('User email not found. Please ensure you are logged in with a valid email address.');
      }
      
      // Try to get existing profile, but don't fail if RLS blocks it
      let profile = null;
      try {
        profile = await getUserProfile(user.id);
      } catch (profileError) {
      }
      
      // Create customer if doesn't exist
      if (!profile?.stripe_customer_id) {
        await createStripeCustomer(
          user.id,
          userEmail,
          user.user_metadata?.full_name || user.user_metadata?.name || undefined
        );
      }
      
      // Create portal session and redirect
      const portalUrl = await createCustomerPortalSession(user.id);
      window.open(portalUrl, '_blank');
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      if (error instanceof Error) {
        alert(`Failed to open billing portal: ${error.message}`);
      } else {
        alert('Failed to open billing portal. Please try again.');
      }
    } finally {
      setIsManagingBilling(false);
    }
  };

  const handleUpdateSettings = async (field: keyof typeof billingSettings, value: any) => {
    if (!user) return;

    try {
      await updateBillingSettingsData({
        [field]: value
      });
    } catch (error) {
      console.error('Failed to save billing settings:', error);
    }
  };

  const handleTestBilling = async () => {
    if (!user) return;
    
    setIsTestingBilling(true);
    setBillingTestResult(null);
    
    try {
      const result = await triggerBillingJob(user.id, true);
      setBillingTestResult(result);
      
      // Refresh billing data after test
      if (result.success) {
        await loadBillingData();
      }
    } catch (error) {
      console.error('Failed to test billing:', error);
      let errorMessage = 'Failed to test billing';
      
      if (error instanceof Error) {
        errorMessage = error.message;
        // Try to extract more details from error
        if (error.message.includes('Failed to fetch') || error.message.includes('network')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          errorMessage = 'Authentication error. Please log out and log back in.';
        } else if (error.message.includes('404') || error.message.includes('Not found')) {
          errorMessage = 'Billing endpoint not found. Please check your API configuration.';
        }
      }
      
      setBillingTestResult({
        success: false,
        error: errorMessage
      });
    } finally {
      setIsTestingBilling(false);
    }
  };

  if (!user) {
    return null;
  }


  const isOverLimit = (currentMonthUsage?.cost || 0) > billingSettings.spending_limit_usd;
  const isNearLimit = (currentMonthUsage?.cost || 0) > (billingSettings.spending_limit_usd * billingSettings.alert_threshold_percent / 100);
  const isSubscriber = subscriptionLoading ? null : (subscriptionInfo?.subscription_status === 'active' || subscriptionInfo?.subscription_status === 'cancelled');

  return (
    <div className="billing-page">
      <PageHeader
        icon={CreditCard}
        title="Billing"
        subtitle="Manage your payment methods and billing settings"
        actions={
          <button
            onClick={handleRefresh}
            className="refresh-button"
            disabled={isRefreshing || settingsLoading || invoicesLoading}
          >
            <RefreshCw className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`} size={16} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        }
      />

      {/* Current Month Usage */}
      <div className="usage-overview-card">
        <div className="card-header">
          <h3>Current Month Usage</h3>
          {(isOverLimit || isNearLimit) && billingSettings.auto_suspend && (
            <div className={`status-badge ${isOverLimit ? 'over-limit' : 'near-limit'}`}>
              {isOverLimit ? (
                <>
                  <XCircle size={16} />
                  Over Limit
                </>
              ) : (
                <>
                  <XCircle size={16} />
                  Near Limit
                </>
              )}
            </div>
          )}
        </div>
        
        <div className="usage-stats">
          <div className="stat-item">
            <div className="stat-label">Total Requests</div>
            <div className="stat-value">{currentMonthUsage?.requests?.toLocaleString() || '0'}</div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Free Requests Used</div>
            <div className="stat-value">
              {subscriptionLoading ? (
                <span className="inline-loading">
                  <div className="loading-spinner small" />
                </span>
              ) : (
                <>
                  {currentMonthUsage?.freeRequestsUsed || 0}/{isSubscriber ? 600 : FREE_REQUESTS_PER_MONTH}
                  {isSubscriber && (
                    <span className="subscription-badge" style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: '#10b981' }}>Subscriber</span>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="stat-item">
            <div className="stat-label">Current Cost</div>
            <div className="stat-value">{formatCurrency(currentMonthUsage?.cost || 0)}</div>
          </div>
        </div>

        {isOverLimit && billingSettings.auto_suspend && (
          <div className="alert-banner over-limit">
            <XCircle size={20} />
            <div>
              <strong>API Access Suspended</strong>
              <p>You've exceeded your spending limit. Please update your payment method or increase your limit to resume API access.</p>
            </div>
          </div>
        )}

        {isNearLimit && !isOverLimit && billingSettings.auto_suspend && (
          <div className="alert-banner near-limit">
            <XCircle size={20} />
            <div>
              <strong>Approaching Spending Limit</strong>
              <p>You've used {Math.round(((currentMonthUsage?.cost || 0) / billingSettings.spending_limit_usd) * 100)}% of your spending limit.</p>
            </div>
          </div>
        )}
      </div>

      {/* Moderation Platform Subscription */}
      <div className="subscription-card">
        <div className="card-header">
          <h3>Moderation Platform Subscription</h3>
          {subscriptionInfo?.subscription_status === 'active' && (
            <div className="status-badge active">
              <CheckCircle2 size={16} />
              Active
            </div>
          )}
        </div>
        
        <div className="subscription-info">
          {subscriptionLoading ? (
            <div className="subscription-loading">
              <div className="loading-spinner" />
              <p>Loading subscription...</p>
            </div>
          ) : (subscriptionInfo?.subscription_status === 'active' || subscriptionInfo?.subscription_status === 'cancelled') ? (
            <>
              <div className="subscription-details">
                <div className="detail-item">
                  <span className="detail-label">Plan:</span>
                  <span className="detail-value">Moderation Platform - $49/month</span>
                </div>
                {subscriptionInfo.subscription_billing_date && (
                  <div className="detail-item">
                    <span className="detail-label">
                      {subscriptionInfo.subscription_status === 'cancelled' ? 'Active Until:' : 'Next Billing Date:'}
                    </span>
                    <span className="detail-value">
                      {(() => {
                        const today = new Date();
                        const billingDay = subscriptionInfo.subscription_billing_date;
                        const nextBilling = new Date(today.getFullYear(), today.getMonth(), billingDay);
                        if (nextBilling < today) {
                          nextBilling.setMonth(nextBilling.getMonth() + 1);
                        }
                        return nextBilling.toLocaleDateString();
                      })()}
                    </span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Benefits:</span>
                  <span className="detail-value">600 free API calls/month, Full moderation dashboard access</span>
                </div>
              </div>
              {subscriptionInfo.subscription_status === 'cancelled' && (
                <div className="cancellation-notice">
                  <div className="cancellation-notice-content">
                    <AlertCircle size={16} />
                    <p>Your subscription has been canceled and will remain active until the end of your current billing period. You can resume your subscription by clicking "Manage Billing" below.</p>
                  </div>

                </div>
              )}
              <p className="subscription-help-text">
                Manage or cancel your subscription through the Manage Billing button below.
              </p>
            </>
          ) : (
            <>
              <div className="subscription-benefits">
                <h4>Subscribe to unlock:</h4>
                <ul>
                  <li>✓ Full moderation dashboard access</li>
                  <li>✓ Automated profile approval/rejection</li>
                  <li>✓ Customizable trust score thresholds</li>
                  <li>✓ Webhook integrations</li>
                  <li>✓ Image settings configuration</li>
                  <li>✓ 500 extra free API calls per month (600 total)</li>
                </ul>
              </div>
              <button
                onClick={handleSubscribe}
                className="subscribe-button"
                disabled={isSubscribing}
              >
                {isSubscribing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Subscribe Now - $49/month
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Manage Billing */}
      <div className="payment-method-card">
        <div className="card-header">
          <h3>Manage Billing</h3>
        </div>
        
        <div className="payment-method-info">
          <div className="payment-method-actions">
            <button 
              onClick={handleManageBilling} 
              className="manage-billing-button"
              disabled={isManagingBilling}
            >
              {isManagingBilling ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Opening...
                </>
              ) : (
                <>
                  <ExternalLink size={16} />
                  Manage Billing
                </>
              )}
            </button>
            <p className="payment-method-help-text">
              Manage your payment methods, billing history, and invoices in Stripe.
            </p>
          </div>
        </div>
      </div>

      {/* Billing History Preview */}
      <div className="billing-history-card">
        <div className="card-header">
          <h3>Billing History</h3>
          <button onClick={handleManageBilling} className="view-all-button">
            View All
            <ExternalLink size={16} />
          </button>
        </div>
        
        <div className="history-preview">
          {invoicesLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading billing history...</p>
            </div>
          ) : billingInvoices.length > 0 ? (
            <>
              {billingInvoices.slice(0, historyItemsToShow).map((invoice, index) => (
                <div key={invoice.id} className="history-item">
                  <div className="history-date">
                    {new Date(invoice.billing_period_start).toLocaleDateString('en-US', { 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </div>
                  <div className="history-amount">{formatCurrency(invoice.amount_usd)}</div>
                  <div className={`history-status ${invoice.status}`}>
                    {invoice.status === 'paid' ? 'Paid' : 
                     invoice.status === 'pending' ? 'Pending' : 
                     invoice.status === 'failed' ? 'Failed' : 'Failed'}
                  </div>
                </div>
              ))}
              {billingInvoices.length > historyItemsToShow && (
                <button
                  onClick={() => setHistoryItemsToShow(prev => prev + 5)}
                  className="show-more-button"
                >
                  Show More
                </button>
              )}
            </>
          ) : (
            <div className="no-history">
              <p>No billing history available</p>
            </div>
          )}
        </div>
      </div>

      {/* API Pricing */}
      <div className="api-pricing-card">
        <div className="card-header">
          <h3>API Pricing</h3>
        </div>
        
        <div className="pricing-grid-new">
          <div className="pricing-card">
            <div className="pricing-card-body">
              <div className="pricing-endpoint-new">/api/moderation/submit-profile</div>
              <div className="pricing-name-new">Submit Profile</div>
              <div className="pricing-cost-new">{formatCurrency(API_PRICING['/api/moderation/submit-profile'] || 0.03)} per request</div>
            </div>
          </div>

          <div className="pricing-card">
            <div className="pricing-card-body">
              <div className="pricing-endpoint-new">/api/face-detect</div>
              <div className="pricing-name-new">Face Detection</div>
              <div className="pricing-cost-new">{formatCurrency(API_PRICING['/api/face-detect'] || 0)} per request</div>
            </div>
          </div>

          <div className="pricing-card">
            <div className="pricing-card-body">
              <div className="pricing-endpoint-new">/api/face-detect-batch</div>
              <div className="pricing-name-new">Batch Face Detection</div>
              <div className="pricing-cost-new">{formatCurrency(API_PRICING['/api/face-detect-batch'] || 0)} per request</div>
            </div>
          </div>

          <div className="pricing-card">
            <div className="pricing-card-body">
              <div className="pricing-endpoint-new">/api/ai-image-detect</div>
              <div className="pricing-name-new">AI Image Detection</div>
              <div className="pricing-cost-new">{formatCurrency(API_PRICING['/api/ai-image-detect'] || 0)} per request</div>
            </div>
          </div>

          <div className="pricing-card">
            <div className="pricing-card-body">
              <div className="pricing-endpoint-new">/api/composite-check</div>
              <div className="pricing-name-new">Composite Check</div>
              <div className="pricing-cost-new">{formatCurrency(API_PRICING['/api/composite-check'] || 0)} per request</div>
            </div>
          </div>

          <div className="pricing-card">
            <div className="pricing-card-body">
              <div className="pricing-endpoint-new">/api/text-analysis</div>
              <div className="pricing-name-new">Text Analysis</div>
              <div className="pricing-cost-new">{formatCurrency(API_PRICING['/api/text-analysis'] || 0)} per request</div>
            </div>
          </div>

          <div className="pricing-card">
            <div className="pricing-card-body">
              <div className="pricing-endpoint-new">/api/trust-score</div>
              <div className="pricing-name-new">Trust Score</div>
              <div className="pricing-cost-new">{formatCurrency(API_PRICING['/api/trust-score'] || 0)} per request</div>
            </div>
          </div>

          <div className="pricing-card">
            <div className="pricing-card-body">
              <div className="pricing-endpoint-new">/api/face-verify</div>
              <div className="pricing-name-new">Face Verify</div>
              <div className="pricing-cost-new">{formatCurrency(API_PRICING['/api/face-verify'] || 0)} per request</div>
            </div>
          </div>
        </div>
        
        <div className="pricing-note-new">
          <p>
            {subscriptionLoading ? (
              <span className="inline-loading">
                <div className="loading-spinner small" />
              </span>
            ) : (
              <>First {isSubscriber ? 600 : FREE_REQUESTS_PER_MONTH} requests per month are free. After that, you'll be charged based on the API endpoints you use.</>
            )}
          </p>
        </div>
      </div>

      {/* Billing Limits */}
      <div className="billing-settings-card">
        <div className="card-header">
          <h3>Billing Limits</h3>
        </div>
        
        <div className="settings-form">
          <div className="setting-item">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={billingSettings.auto_suspend}
                onChange={(e) => handleUpdateSettings('auto_suspend', e.target.checked)}
                disabled={settingsLoading}
              />
              <span>Automatically suspend API access when limit is reached</span>
            </label>
          </div>

          <div className="setting-item spending-limit-section">
            <label htmlFor="spending-limit">Monthly Spending Limit</label>
            <div className="input-group">
              <DollarSign size={16} />
              <input
                id="spending-limit"
                type="number"
                min="0"
                step="0.01"
                value={billingSettings.spending_limit_usd}
                onChange={(e) => handleUpdateSettings('spending_limit_usd', parseFloat(e.target.value))}
                disabled={settingsLoading || !billingSettings.auto_suspend}
              />
            </div>
            <p className="setting-description">
              API calls will be suspended when this limit is reached
            </p>
          </div>
        </div>
      </div>

      {/* Test Billing Job */}
      {/* <div className="billing-settings-card">
        <div className="card-header">
          <h3>Test Billing</h3>
        </div>
        
        <div className="settings-form">
          <div className="setting-item">
            <p className="setting-description">
              Test the monthly billing job. This will create an invoice for your current month's usage and attempt to charge your payment method.
            </p>
            <button
              onClick={handleTestBilling}
              disabled={isTestingBilling}
              className="test-billing-button"
            >
              {isTestingBilling ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <RefreshCw size={16} />
                  Test Monthly Billing
                </>
              )}
            </button>
            
            {billingTestResult && (
              <div className={`billing-test-result ${billingTestResult.success ? 'success' : 'error'}`}>
                {billingTestResult.success ? (
                  <>
                    <CheckCircle2 size={20} />
                    <div>
                      <strong>Invoice Created Successfully</strong>
                      {billingTestResult.invoice && (
                        <div className="test-result-details">
                          <p>Invoice ID: {billingTestResult.invoice.id}</p>
                          <p>Amount: {formatCurrency(billingTestResult.invoice.amount)}</p>
                          <p>Status: {billingTestResult.invoice.status}</p>
                          {billingTestResult.invoice.hosted_invoice_url && (
                            <a 
                              href={billingTestResult.invoice.hosted_invoice_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="invoice-link"
                            >
                              <ExternalLink size={14} />
                              View Invoice
                            </a>
                          )}
                          {billingTestResult.usage && (
                            <p className="usage-info">
                              Usage: {billingTestResult.usage.totalRequests} requests, {formatCurrency(billingTestResult.usage.totalCost)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <AlertCircle size={20} />
                    <div>
                      <strong>Test Failed</strong>
                      <p>{billingTestResult.error || billingTestResult.message || 'Unknown error'}</p>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default BillingPage;
